import { useEffect, useMemo, useState } from 'react';
import { intakes as intakesApi, mapy, ocenaRyzyka as riskApi } from '@/api/endpoints';
import type { Intake, MapLayer, RiskAssessment, RiskCatchment, RiskPressureFeatureGeo, RiskProtectionZone } from '@/types';
import { MapLibreMap, PSH_MIN_ZOOM, type PshState } from '@/components/MapLibreMap';
import { SearchableSelect } from '@/components/SearchableSelect';

/** Opis stanu warstwy otworów PSH przy przełączniku (uwaga 4 z 30.07.2026). */
function pshStanOpis(s: PshState | null): string {
  switch (s?.status) {
    case 'zoom': return ` — przybliż mapę (zoom ≥ ${PSH_MIN_ZOOM}), aby wczytać`;
    case 'loading': return ' — wczytywanie z CBDG…';
    case 'ok': return ` — ${s.count} w widoku`;
    case 'empty': return ' — brak otworów w tym widoku';
    case 'error': return ' — usługa CBDG nie odpowiada';
    default: return '';
  }
}

function pshStanKlasa(s: PshState | null): string {
  if (s?.status === 'error') return 'text-red-500';
  if (s?.status === 'zoom' || s?.status === 'empty') return 'text-amber-600';
  return 'text-ink-400';
}

const CATCHMENT_COLOR = '#0e7490';
const INTAKE_COLOR = '#06B6D4';
const PRESSURE_COLOR = '#ef4444';

const STRATEGY_LABEL: Record<string, string> = {
  SICHARDT_BUFFER: 'Bufor Sichardta', DOC: 'Dokumentacja hydrogeologiczna',
  MPHP: 'Zlewnia MPHP10k', WYSSLING: 'Strefa Wysslinga',
  IMPORTED: 'Wgrany obszar (OSW)', DRAWN: 'Narysowany ręcznie',
};

/** Wpis stosu warstw katalogowych (WMS/WMTS/XYZ z rejestru map_layer). */
type StackEntry = { code: string; visible: boolean; opacity: number };

/**
 * Mapa poglądowa oceny ryzyka (art. 4f): ujęcie + strefa oddziaływania (obszar zasilania) + otwory PSH,
 * ORAZ pełny stos WARSTW KATALOGOWYCH (WMS/WMTS z rejestru map_layer) z panelem widoczność/krycie — tak,
 * by komplet oceny nie wymagał przechodzenia na „Mapy · GIS". Warstwy katalogowe renderują się POD overlayami
 * oceny (silnik MapLibreMap: overlayAnchor), więc strefa/presje/otwory/rysowany OSW zawsze pozostają na wierzchu.
 */
export function OcenaMap({ ra, catchment, protectionZones = [], onSaveGeometry, overlayRefreshKey, focusGeometry = null, highlightGeometry = null, wellPoints = [], hazardPoints = [], canEdit = false, onPickHazard }: {
  ra: RiskAssessment; catchment: RiskCatchment | null;
  /** Wgrane granice stref ochronnych ujęcia (F4) — bursztynowy kreskowany obrys. */
  protectionZones?: RiskProtectionZone[];
  /** Zapis narysowanego obszaru OSW (string GeoJSON) — wywołuje przeliczenie presji (weryfikacja obszaru). */
  onSaveGeometry?: (geoJson: string, strategy?: 'IMPORTED' | 'DRAWN') => void;
  /** Bump wymusza ponowny odczyt overlay presji (np. po „Pobierz warstwę dla tej oceny"). */
  overlayRefreshKey?: number;
  /** Dolot mapy do geometrii obiektu wskazanego z listy presji (nonce wymusza powtórzenie). */
  focusGeometry?: { geoJson: string; nonce: number } | null;
  /** Geometria obiektu wyróżnionego na mapie (utrzymywana, dopóki obiekt jest wybrany na liście). */
  highlightGeometry?: string | null;
  /** Studnie ujęcia — punkty na mapie z oznaczeniem. */
  wellPoints?: { designation: string; latitude: number | null; longitude: number | null }[];
  /** Własne zagrożenia wskazane na mapie — punkty z tytułem i kolorem kategorii. */
  hazardPoints?: { id: number; title: string; categoryColor?: string | null; latitude: number | null; longitude: number | null }[];
  /** Czy użytkownik może edytować (włącza przycisk „Wskaż zagrożenie na mapie"). */
  canEdit?: boolean;
  /** Klik w mapę w trybie wskazywania zagrożenia → współrzędne WGS84 wybranego punktu. */
  onPickHazard?: (lng: number, lat: number) => void;
}) {
  const [intake, setIntake] = useState<Intake | null>(null);
  const [loading, setLoading] = useState(false);
  const [pressures, setPressures] = useState<RiskPressureFeatureGeo[]>([]);
  const [showZone, setShowZone] = useState(true);
  const [showProtection, setShowProtection] = useState(true);
  const [showPressure, setShowPressure] = useState(true);
  const [showPsh, setShowPsh] = useState(false);
  const [pshState, setPshState] = useState<PshState | null>(null);
  const [drawing, setDrawing] = useState(false);
  // Tryb wskazywania własnego zagrożenia (krzyżyk + jeden klik → onPickHazard). Wyklucza się z rysowaniem.
  const [picking, setPicking] = useState(false);
  // Warstwy katalogowe (WMS/WMTS) — stos + katalog + panel + wskaźnik doczytywania kafelków.
  const [catalog, setCatalog] = useState<MapLayer[]>([]);
  const [stack, setStack] = useState<StackEntry[]>([]);
  const [showLayers, setShowLayers] = useState(false);
  const [layerLoading, setLayerLoading] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!ra.intakeId) { setIntake(null); return; }
    let live = true;
    setLoading(true);
    intakesApi.get(ra.intakeId)
      .then(v => { if (live) setIntake(v); })
      .catch(() => { if (live) setIntake(null); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [ra.intakeId]);

  // Katalog warstw + kurowany zestaw 4f (raz) — seed stosu: kody z kuracji obecne w katalogu, z domyślną widocznością.
  useEffect(() => {
    let live = true;
    Promise.all([mapy.layers().catch(() => [] as MapLayer[]), riskApi.mapLayers().catch(() => [])])
      .then(([cat, curated]) => {
        if (!live) return;
        setCatalog(cat);
        const byCode = new Map(cat.map(l => [l.code, l] as [string, MapLayer]));
        setStack(curated
          .filter(c => byCode.has(c.code))
          .map(c => ({ code: c.code, visible: c.visible, opacity: byCode.get(c.code)!.defaultOpacity ?? 80 })));
      });
    return () => { live = false; };
  }, []);

  // Ponowny odczyt presji także gdy zmieni się geometria strefy (import/reset OSW → overlay przeliczony).
  useEffect(() => {
    let live = true;
    riskApi.overlayGeoJson(ra.id)
      .then(v => { if (live) setPressures(v); })
      .catch(() => { if (live) setPressures([]); });
    return () => { live = false; };
  }, [ra.id, catchment?.geoJson, overlayRefreshKey]);

  // Stabilna referencja overlay-a: zmienia się tylko przy zmianie geometrii/widoczności (nie co render).
  const catchmentOverlay = useMemo(
    () => (showZone && catchment?.geoJson ? { geoJson: catchment.geoJson, color: CATCHMENT_COLOR } : null),
    [showZone, catchment?.geoJson],
  );

  // F4: granice stref ochronnych (import aktu wojewody) — geometrie 4326 z backendu
  const protectionOverlay = useMemo<GeoJSON.FeatureCollection | null>(() => {
    if (!showProtection || protectionZones.length === 0) return null;
    const features: GeoJSON.Feature[] = [];
    for (const z of protectionZones) {
      try {
        features.push({ type: 'Feature', properties: { kind: z.kind }, geometry: JSON.parse(z.geoJson) as GeoJSON.Geometry });
      } catch { /* niepoprawna geometria — pomiń */ }
    }
    return features.length ? { type: 'FeatureCollection', features } : null;
  }, [showProtection, protectionZones]);

  const pressureOverlay = useMemo<GeoJSON.FeatureCollection | null>(() => {
    if (!showPressure || pressures.length === 0) return null;
    const feats = pressures.flatMap((p, i) => {
      try {
        return [{
          type: 'Feature' as const, id: i,
          properties: { layer: p.sourceLayer, name: p.featureName ?? '', distanceM: p.distanceM, intersects: p.intersects },
          geometry: JSON.parse(p.geoJson) as GeoJSON.Geometry,
        }];
      } catch { return []; }
    });
    return feats.length ? { type: 'FeatureCollection', features: feats } : null;
  }, [showPressure, pressures]);

  // Warstwy katalogowe: mapa kod→warstwa + rozwiązany stos dla MapLibreMap (odrzuca osierocone kody).
  const byCode = useMemo(() => new Map(catalog.map(l => [l.code, l] as [string, MapLayer])), [catalog]);
  const renderStack = useMemo<{ layer: MapLayer; opacity: number; visible: boolean }[]>(
    () => stack.flatMap(s => { const layer = byCode.get(s.code); return layer ? [{ layer, opacity: s.opacity, visible: s.visible }] : []; }),
    [stack, byCode],
  );
  const visibleLayerCount = useMemo(() => stack.filter(s => s.visible && byCode.has(s.code)).length, [stack, byCode]);
  const catalogOptions = useMemo(
    () => catalog.filter(l => !stack.some(s => s.code === l.code))
      .map(l => ({ value: l.code, label: l.name, group: l.category })),
    [catalog, stack],
  );

  // Zdjęcia satelitarne = ortofotomapa GUGiK. Preferuj WMTS wysokiej rozdzielczości; fallback do WMTS/WMS
  // zależnie od tego, co jest w katalogu. Osobny, wyróżniony przełącznik (nie trzeba wchodzić w katalog).
  const satCode = useMemo(() => ['ORTO_HIRES_WMTS', 'ORTO_WMTS', 'ORTO'].find(c => byCode.has(c)) ?? null, [byCode]);
  const satOn = useMemo(() => !!satCode && stack.some(s => s.code === satCode && s.visible), [satCode, stack]);
  const toggleSat = () => {
    if (!satCode) return;
    setStack(s => s.some(x => x.code === satCode)
      // istnieje w stosie → przełącz widoczność, przy włączeniu podbij krycie do pełnego (podkład)
      ? s.map(x => x.code === satCode ? { ...x, visible: !x.visible, opacity: x.visible ? x.opacity : 100 } : x)
      // brak w stosie → dołóż jako widoczny podkład na pełnym kryciu
      : [{ code: satCode, visible: true, opacity: 100 }, ...s]);
  };

  const toggleLayer = (code: string) => setStack(s => s.map(x => x.code === code ? { ...x, visible: !x.visible } : x));
  const setLayerOpacity = (code: string, op: number) => setStack(s => s.map(x => x.code === code ? { ...x, opacity: op } : x));
  const addLayer = (code: string) => setStack(s => s.some(x => x.code === code)
    ? s : [{ code, visible: true, opacity: byCode.get(code)?.defaultOpacity ?? 80 }, ...s]);
  const removeLayer = (code: string) => setStack(s => s.filter(x => x.code !== code));

  // Marker: powiązane ujęcie, a dla oceny bez ujęcia — syntetyczny punkt z współrzędnych oceny.
  const markerIntake: Intake | null = intake ?? (
    ra.latitude != null && ra.longitude != null
      ? ({ latitude: ra.latitude, longitude: ra.longitude, name: ra.objectName ?? ra.number, town: ra.town ?? undefined } as unknown as Intake)
      : null
  );
  const hasCoords = markerIntake?.latitude != null && markerIntake?.longitude != null;

  return (
    <div className="card">
      <div className="card-head flex items-center gap-2">
        <span className="card-title">Mapa poglądowa</span>
        <span className="text-[11px] text-ink-400">{ra.intakeId ? 'ujęcie · ' : ''}strefa oddziaływania · otwory · warstwy</span>
      </div>
      <div className="card-body grid gap-2">
        {ra.intakeId && loading && !intake ? (
          <div className="text-[13px] text-ink-400 py-4 text-center">Ładowanie ujęcia…</div>
        ) : !hasCoords ? (
          <div className="text-[13px] text-amber-600 py-4 text-center">
            Brak współrzędnych — uzupełnij dane ujęcia w zakładce „Dane wejściowe” (ręcznie lub odczytem
            z dokumentacji), aby zobaczyć mapę.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-4 flex-wrap text-[12px]">
              <span className="text-ink-400">Warstwy oceny:</span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full inline-block" style={{ background: INTAKE_COLOR }} /> Ujęcie
              </span>
              <label className={'flex items-center gap-1.5 ' + (catchment?.geoJson ? 'cursor-pointer' : 'opacity-50')}>
                <input type="checkbox" checked={showZone && !!catchment?.geoJson} disabled={!catchment?.geoJson}
                  onChange={e => setShowZone(e.target.checked)} />
                <span className="w-3 h-3 rounded-sm inline-block" style={{ background: CATCHMENT_COLOR, opacity: 0.5 }} />
                Strefa oddziaływania{!catchment?.geoJson ? ' (wygeneruj ocenę)' : ''}
              </label>
              <label className={'flex items-center gap-1.5 ' + (protectionZones.length ? 'cursor-pointer' : 'opacity-50')}>
                <input type="checkbox" checked={showProtection && protectionZones.length > 0} disabled={protectionZones.length === 0}
                  onChange={e => setShowProtection(e.target.checked)} />
                <span className="w-3 h-3 rounded-sm inline-block border border-dashed" style={{ borderColor: '#b45309', background: '#d97706', opacity: 0.4 }} />
                Strefy ochronne{protectionZones.length ? ` (${protectionZones.length})` : ' (brak — wgraj granicę)'}
              </label>
              <label className={'flex items-center gap-1.5 ' + (pressures.length ? 'cursor-pointer' : 'opacity-50')}>
                <input type="checkbox" checked={showPressure && pressures.length > 0} disabled={pressures.length === 0}
                  onChange={e => setShowPressure(e.target.checked)} />
                <span className="w-3 h-3 rounded-sm inline-block" style={{ background: PRESSURE_COLOR, opacity: 0.6 }} />
                Presje w zlewni{pressures.length ? ` (${pressures.length})` : ''}
              </label>
              {/* uwaga 4: warstwa mówi, co się z nią dzieje — bez tego „za daleko", „brak otworów"
                  i awaria usługi CBDG wyglądały identycznie: jako pusta mapa */}
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={showPsh} onChange={e => setShowPsh(e.target.checked)} />
                Otwory PSH (CBDG){showPsh && <span className={pshStanKlasa(pshState)}>{pshStanOpis(pshState)}</span>}
              </label>
              <label className={'flex items-center gap-1.5 ' + (satCode ? 'cursor-pointer' : 'opacity-50')}
                title={satCode ? 'Ortofotomapa GUGiK jako podkład' : 'Warstwa ortofotomapy niedostępna w katalogu'}>
                <input type="checkbox" checked={satOn} disabled={!satCode} onChange={toggleSat} />
                <span className="w-3 h-3 rounded-sm inline-block" style={{ background: 'linear-gradient(135deg,#166534,#a16207)' }} />
                Zdjęcia satelitarne{!satCode ? ' (brak)' : ''}
              </label>
              <div className="ml-auto flex items-center gap-2">
                {canEdit && onPickHazard && (
                  <button type="button" onClick={() => { setPicking(p => !p); setDrawing(false); }}
                    className={'btn btn-sm ' + (picking ? 'btn-primary' : 'btn-secondary')}>
                    {picking ? 'Anuluj wskazywanie' : 'Wskaż zagrożenie na mapie'}
                  </button>
                )}
                {onSaveGeometry && (
                  <button type="button" onClick={() => { setDrawing(d => !d); setPicking(false); }}
                    className={'btn btn-sm ' + (drawing ? 'btn-primary' : 'btn-secondary')}>
                    {drawing ? 'Anuluj rysowanie' : 'Rysuj obszar (OSW)'}
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 text-[12px] border-t border-ink-100 pt-2">
              <button type="button" onClick={() => setShowLayers(v => !v)}
                className="flex items-center gap-1 text-ink-600 hover:text-ink-900">
                <span className={'inline-block transition-transform ' + (showLayers ? 'rotate-90' : '')}>▸</span>
                Warstwy katalogowe (mapy · GIS){visibleLayerCount ? ` — ${visibleLayerCount} widoczne` : ''}
              </button>
            </div>

            {showLayers && (
              <div className="grid gap-1.5 rounded-md border border-ink-100 bg-ink-50/40 p-2 text-[12px]">
                {stack.length === 0 && <div className="text-ink-400">Brak warstw w stosie — dodaj z katalogu poniżej.</div>}
                {stack.map(s => {
                  const layer = byCode.get(s.code);
                  if (!layer) return null;
                  return (
                    <div key={s.code} className="flex items-center gap-2">
                      <input type="checkbox" checked={s.visible} onChange={() => toggleLayer(s.code)} title="Widoczność" />
                      <span className="min-w-0 flex-1 truncate" title={`${layer.name} · ${layer.category} · ${layer.protocol}`}>
                        {layer.name}
                        {layerLoading.has(s.code) && <span className="ml-1 text-water-500 animate-pulse">…</span>}
                      </span>
                      <input type="range" min={0} max={100} value={s.opacity} title="Krycie"
                        className="w-20 accent-water-600" disabled={!s.visible}
                        onChange={e => setLayerOpacity(s.code, Number(e.target.value))} />
                      <button type="button" onClick={() => removeLayer(s.code)} title="Usuń warstwę"
                        className="text-ink-400 hover:text-red-600 px-1">×</button>
                    </div>
                  );
                })}
                <div className="pt-1">
                  <SearchableSelect value="" onChange={c => c && addLayer(c)} options={catalogOptions}
                    placeholder={catalogOptions.length ? 'Dodaj warstwę z katalogu…' : 'Katalog warstw niedostępny'} />
                </div>
              </div>
            )}

            {drawing && (
              <div className="text-[11px] text-water-600">
                Rysowanie obszaru: <b>klik</b> = wierzchołek, <b>podwójny klik</b> = zakończ (min. 3), <b>Esc</b> = anuluj.
                Po zakończeniu obszar zostanie zapisany i presje przeliczone dla zaznaczonego obszaru.
              </div>
            )}

            {picking && (
              <div className="text-[11px] text-water-600">
                Wskazywanie zagrożenia: <b>klik</b> na mapie ustawia punkt — pojawi się formularz z tytułem i kategorią.
              </div>
            )}

            <MapLibreMap
              intake={markerIntake}
              markerMode="intake"
              catchmentOverlay={catchmentOverlay}
              protectionOverlay={protectionOverlay}
              pressureOverlay={pressureOverlay}
              focusGeometry={focusGeometry}
              highlightGeometry={highlightGeometry}
              wellPoints={wellPoints}
              hazardPoints={hazardPoints}
              pickPointMode={picking}
              onPointPick={(lng, lat) => { onPickHazard?.(lng, lat); setPicking(false); }}
              showPsh={showPsh}
              onPshState={setPshState}
              layerStack={renderStack}
              onLayerLoadingChange={setLayerLoading}
              drawPolygonMode={drawing}
              onPolygonComplete={poly => { onSaveGeometry?.(JSON.stringify(poly), 'DRAWN'); setDrawing(false); }}
              height="440px"
            />

            {catchment && (
              <div className="text-[11px] text-ink-400">
                Strefa: {STRATEGY_LABEL[catchment.strategy] ?? catchment.strategy}
                {catchment.radiusM != null ? ` · R ${catchment.radiusM} m` : ''}
                {catchment.areaKm2 != null ? ` · ${catchment.areaKm2.toFixed(2)} km²` : ''}
                {catchment.provisional ? ' · prowizoryczna' : ''}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
